import React from "react";
import { LockSimple } from "@phosphor-icons/react";
import { Tooltip } from "@mui/material";

interface LockedFileIndicatorProps {
  /**
   * The file's task status. The indicator hides itself once the file has
   * been processed ("completed" or "partially-completed"): by then the
   * password is stored and the file is no longer locked from the user's
   * point of view.
   */
  status?: string;
}

const isUnlockedStatus = (status: string | undefined): boolean =>
  status === "completed" || status === "partially-completed";

const LOCKED_FILE_LABEL = "Password-protected";

/** A passive mark beside a locked file's name: it says the file is locked and
 *  nothing else. Opening the file is where it gets unlocked (the viewer's
 *  password panel), so the mark is not a control (#11856).
 *
 *  Claude Design ruling: 14px LockSimple to match the 14px name, secondary
 *  ink (#6E7480) rather than a lighter gray that reads as a smudge, after the
 *  name with a 6px gap and never wrapping away from it, the name truncating
 *  and the glyph not. The tooltip is the glyph's accessible name surfacing
 *  on hover, the same string as its aria-label; the cursor stays the row's.
 *  It replaced a clickable badge that opened a browser prompt. */
const LockedFileIndicator: React.FC<LockedFileIndicatorProps> = ({
  status,
}) => {
  if (isUnlockedStatus(status)) return null;
  return (
    <Tooltip title={LOCKED_FILE_LABEL} placement="top">
      <span className="inline-flex shrink-0 items-center">
        <LockSimple
          size={14}
          weight="regular"
          role="img"
          aria-label={LOCKED_FILE_LABEL}
          className="text-[#6E7480]"
        />
      </span>
    </Tooltip>
  );
};

export default LockedFileIndicator;
